/**
 * Serviço de Processamento de Arquivos
 * Processa imagens e PDFs para extração de texto e dados
 */

// Edge Function URL - chave OpenAI fica no servidor (seguro)
const EDGE_AI_URL = import.meta.env.VITE_EDGE_AI_URL || '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

export interface FileProcessingResult {
  extractedText: string;
  category?: string;
  amount?: number;
  date?: string;
  merchant?: string;
  type?: 'expense' | 'income';
}

/**
 * Converte arquivo para base64
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Processa uma imagem de comprovante usando Vision API
 * @param file - Arquivo de imagem (JPG, PNG, etc.)
 * @returns Dados extraídos do comprovante
 */
export async function processReceiptImage(file: File): Promise<FileProcessingResult> {
  try {
    const base64Image = await fileToBase64(file);
    const mimeType = file.type || 'image/jpeg';

    if (!EDGE_AI_URL) {
      throw new Error('Edge Function não configurada. Configure VITE_EDGE_AI_URL no arquivo .env');
    }

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (SUPABASE_ANON_KEY) {
      headers['apikey'] = SUPABASE_ANON_KEY;
      headers['Authorization'] = `Bearer ${SUPABASE_ANON_KEY}`;
    }

    const response = await fetch(EDGE_AI_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({ imageDataUrl: `data:${mimeType};base64,${base64Image}` })
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Erro ao processar imagem: ${error}`);
    }

    const result = await response.json();
    const content = result.choices?.[0]?.message?.content ?? '';
    
    // Extrair JSON da resposta
    const jsonMatch = typeof content === 'string' ? content.match(/\{[\s\S]*\}/) : null;
    if (jsonMatch) {
      return JSON.parse(jsonMatch[0]);
    }

    throw new Error('Não foi possível extrair dados do comprovante');
  } catch (error) {
    console.error('Erro ao processar imagem:', error);
    throw error;
  }
}

/**
 * Valida se o arquivo é suportado
 */
export function isFileSupported(file: File): boolean {
  const supportedTypes = [
    'image/jpeg',
    'image/jpg', 
    'image/png',
    'image/webp',
    'application/pdf'
  ];
  
  const maxSize = 10 * 1024 * 1024; // 10MB
  
  return supportedTypes.includes(file.type) && file.size <= maxSize;
}

/**
 * Obtém mensagem de erro para arquivo não suportado
 */
export function getFileErrorMessage(file: File): string {
  if (file.size > 10 * 1024 * 1024) {
    return 'Arquivo muito grande. Tamanho máximo: 10MB';
  }
  
  return 'Formato não suportado. Use: JPG, PNG, WEBP ou PDF';
}
